import { useEffect, useRef } from 'react'

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Keyboard behaviour for a slide-in panel used as a modal (the phone menu):
 * focus moves into it when it opens, Tab stays inside, Escape closes it and
 * focus returns to whatever opened it. While closed it is inert, so its links
 * are not tab stops hidden off-screen.
 */
export function useDrawerFocus<T extends HTMLElement>(open: boolean, onClose: () => void) {
  const panel = useRef<T>(null)

  useEffect(() => {
    const node = panel.current
    if (!node) return
    ;(node as HTMLElement & { inert: boolean }).inert = !open
    if (!open) return

    const opener = document.activeElement as HTMLElement | null
    const items = () => Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE))
    items()[0]?.focus()

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
        return
      }
      if (event.key !== 'Tab') return
      const list = items()
      if (!list.length) return
      const first = list[0]
      const last = list[list.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      opener?.focus?.()
    }
  }, [open, onClose])

  return panel
}
